import { useState } from 'react';
import { useTranslation } from 'react-i18next';
// Self-sufficient, as Onboarding.jsx is: this screen renders BEFORE it, so
// relying on that import to have initialised i18n would leave the copy as raw
// key names the moment the module graph changes.
import '../../i18n';
import { Link } from 'react-router-dom';
import { Button, Icon, Select } from '@shopify/polaris';
import { ArrowRightIcon, StarFilledIcon } from '@shopify/polaris-icons';
import useAxiosClient from '../axiosClient';
// The same mark the footer and the setup guide draw. Two copies of a brand mark
// drift apart the first time someone retouches one of them.
import Wordmark from '../Wordmark';
import { isWix } from '../../utils/platform';
import { openSupport } from '../../support';
// Figures from the one module that owns them. Note this app's REVIEWS has NO
// builtForShopify key and its own docstring says why: that badge is awarded to
// the Shopify listing, not to this install, so it is not shown here.
import { REVIEWS } from '../Pricing/PricingTrustFaq';

import heroImage from '../../images/welcome/01_hero_retail_clockin.png';
import addTeamArt from '../../images/welcome/02_add_team.png';
import trackTimeArt from '../../images/welcome/03_track_time.png';
import shiftsArt from '../../images/welcome/04_shifts_schedule.png';
import payrollArt from '../../images/welcome/05_payroll_ready.png';
import timeOffArt from '../../images/welcome/06_manage_time_off.png';
import reportsArt from '../../images/welcome/07_view_reports.png';

/**
 * The first screen after an install, before the setup guide.
 *
 * It exists because the guide opens by asking for work - add a staff member,
 * choose a clock-in method - without ever having said what the product does. A
 * merchant who installed on a recommendation and has not seen the listing in a
 * week arrives at a form. This says what they bought first, then hands over.
 *
 * THIS IS THE SHOPIFY APP'S SCREEN, PORTED, with three differences that are not
 * cosmetic and must not be "tidied" back into line:
 *
 *   NO SHOPIFY POS. This build serves Wix, Square, Clover, AppSumo and direct
 *   logins. The Shopify copy for the clock-in card names the POS as a second
 *   way to clock in; here it cannot, because for most of these merchants there
 *   is no POS.
 *
 *   NO "BUILT FOR SHOPIFY". PricingTrustFaq in this app deliberately omits that
 *   flag - the badge belongs to the Shopify listing, not to this install. The
 *   proof strip therefore carries the rating and the review count only, and it
 *   says on its face that they come from the Shopify App Store, so a Wix
 *   merchant is not left to assume they were left by users of this build.
 *
 *   THE PRICING LINK IS REMOVED ON WIX, not hidden. App Market apps must bill
 *   through Wix Billing, and pointing a Wix merchant at our own checkout is a
 *   documented rejection trigger.
 *
 * TYPE AND COLOUR are the guide's: ink #303030, muted #616161, borders #e3e3e3,
 * wells #f6f6f7, accent #008060, radii 8 and 12.
 */

const C = {
  ink: '#303030',
  muted: '#616161',
  faint: '#8a8a8a',
  border: '#e3e3e3',
  surface: '#ffffff',
  accent: '#008060',
};

// Same icon the guide uses.
const APP_ICON_URL =
  'https://cdn.shopify.com/app-store/listing_images/b4cbee22295ba0ec0d6538ebb0fc25f4/icon/CLOh2sjy2YwDEAE=.png';

/** The list the guide and the dashboard switcher both offer, in their order. */
const LANGUAGES = [
  { label: 'English', value: 'en' },
  { label: 'German', value: 'de' },
  { label: 'Spanish', value: 'es' },
  { label: 'French', value: 'fr' },
  { label: 'Italian', value: 'it' },
  { label: 'Dutch', value: 'nl' },
  { label: 'Japanese', value: 'ja' },
  { label: 'Chinese', value: 'zh-CN' },
  { label: 'Portuguese', value: 'pt' },
];

const FEATURES = [
  { key: 'staff', art: addTeamArt },
  { key: 'clock', art: trackTimeArt },
  { key: 'schedule', art: shiftsArt },
  { key: 'payroll', art: payrollArt },
  { key: 'timeoff', art: timeOffArt },
  { key: 'reports', art: reportsArt },
];

export default function Welcome({ onStart }) {
  const { t, i18n } = useTranslation();
  const axiosClient = useAxiosClient();

  const [language, setLanguage] = useState(() => {
    try {
      return localStorage.getItem('zonstaff_language') || 'en';
    } catch {
      // Blocked storage costs the preference, not the screen.
      return 'en';
    }
  });

  /**
   * Mirrors the guide's handler exactly - same storage key, same endpoint - so
   * a language chosen here is still in force on the screen it hands over to.
   */
  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setLanguage(lng);

    try {
      localStorage.setItem('zonstaff_language', lng);
    } catch {
      // A blocked store only costs us the preference on the next load.
    }

    axiosClient.post('/update_language', { language: lng }).catch(() => {});
  };

  // 1000, and NOT wider. This looks like wasted margin on a 1920 screen and it
  // was measured before being left alone.
  //
  // German is the binding language - its six descriptions are the longest - and
  // it only wraps UNIFORMLY in two narrow bands. Measured across the six cards:
  //
  //     1000          2,2,2,2,2,2   even
  //     1040 - 1240   mixed, e.g. 2,2,1,2,1,2 - a ragged row bottom
  //     1320 - 1380   1,1,1,1,1,1   even
  //     above ~1390   the hero upscales past its natural 636px and goes soft
  //
  // The upper band cannot be used. Any maxWidth above about 1224 is
  // viewport-limited on the laptops merchants actually use - 1280 and 1366 land
  // inside the ragged band - so the layout would be even on the machine it was
  // designed on and ragged on theirs. 1000 is below every common viewport, so
  // it renders identically on all of them.
  //
  // That raggedness is the exact complaint this screen was already fixed for
  // once. Widening trades a quiet margin for it.
  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', paddingTop: '8px' }}>
      <div
        style={{
          background: 'linear-gradient(115deg, #f1faf5 0%, #ffffff 62%)',
          border: `1px solid ${C.border}`,
          borderRadius: '12px',
          padding: '28px',
        }}
      >
        {/* Brand at the start, the one control at the end - the same header the
            setup guide draws, because the two screens are seen back to back and
            a control that moves between them reads as a different control.

            INSIDE the card. Floating above it, the switcher was the only thing
            on its row with nothing anchoring the other end, so it read as
            chrome hanging in space rather than part of the page.

            flexWrap, as the guide's header has: on a narrow screen the switcher
            drops to its own line instead of crushing the wordmark. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            flexWrap: 'wrap',
            marginBottom: '20px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img
              src={APP_ICON_URL}
              alt=""
              width="30"
              height="30"
              style={{ borderRadius: '7px' }}
              onError={(event) => {
                // Served from a CDN; if it does not arrive the mark beside
                // it still reads.
                event.currentTarget.style.display = 'none';
              }}
            />
            <Wordmark className="text-[24px]" />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>
              &#127760; {t('select_language')}
            </span>
            <Select
              label=""
              labelInline
              options={LANGUAGES}
              value={language}
              onChange={(value) => changeLanguage(value)}
            />
          </div>
        </div>

        <div className="welcome-hero">
          <div>
            <h1
              style={{
                margin: '0',
                fontSize: '28px',
                lineHeight: 1.2,
                fontWeight: 700,
                letterSpacing: '-0.5px',
                color: C.ink,
                textWrap: 'balance',
              }}
            >
              {t('welcome_headline')}
            </h1>

            <p
              style={{
                margin: '10px 0 0',
                maxWidth: '44ch',
                fontSize: '14px',
                lineHeight: 1.55,
                color: C.muted,
                textWrap: 'balance',
              }}
            >
              {t('welcome_subhead')}
            </p>

            {/* The same construction as the guide's primary action, rather than
                a hand-rolled button that would inherit none of Polaris' focus
                ring, pressed state or disabled handling. */}
            <div style={{ marginTop: '18px' }}>
              <Button variant="primary" icon={ArrowRightIcon} onClick={onStart}>
                {t('welcome_cta')}
              </Button>
            </div>

            <p style={{ margin: '10px 0 0', fontSize: '12.5px', color: C.faint }}>
              {t('welcome_reassurance')}
            </p>

            {/* Proof at the point of the decision. It names the Shopify App
                Store on its face, because these figures come from that listing
                and most merchants on this build did not install from it. The
                whole strip links there so the claim is checkable in one click. */}
            <a
              href={REVIEWS.listingUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                marginTop: '14px',
                fontSize: '12.5px',
                color: C.muted,
                textDecoration: 'none',
              }}
            >
              <span style={{ display: 'inline-flex', width: '14px', height: '14px', color: '#ffb800' }}>
                <Icon source={StarFilledIcon} tone="inherit" />
              </span>
              {t('pricing_trust_subtitle', { rating: REVIEWS.rating, reviews: REVIEWS.count })}
            </a>
          </div>

          <img
            src={heroImage}
            alt=""
            fetchPriority="high"
            decoding="async"
            style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '10px' }}
          />
        </div>
      </div>

      <div className="welcome-grid">
        {FEATURES.map(({ key, art }) => (
          <div
            key={key}
            style={{
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: '12px',
              padding: '12px',
              // A column, so the slack the grid hands this card can be given to
              // the description rather than left in a heap beneath it.
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Fixed-height well - see .welcome-shot. The exports vary in
                aspect, and letting each set its own height left every caption
                on a different line. */}
            <div className="welcome-shot">
              <img src={art} alt="" loading="lazy" decoding="async" />
            </div>

            <h2
              style={{
                margin: '13px 0 0 4px',
                fontSize: '14.5px',
                fontWeight: 650,
                letterSpacing: '-0.2px',
                color: C.ink,
              }}
            >
              {t(`welcome_${key}_title`)}
            </h2>
            <p
              style={{
                margin: '4px 4px 6px',
                fontSize: '13px',
                lineHeight: 1.5,
                color: C.muted,
                textWrap: 'balance',
                // No min-height: the six strings sit in a tight character band
                // in every language, so they wrap together. flex:1 absorbs
                // whatever the grid stretches this card by.
                flex: 1,
              }}
            >
              {t(`welcome_${key}_body`)}
            </p>
          </div>
        ))}
      </div>

      {/* The masthead's action, repeated where the reading ends, with the line
          that says what it leads to beside it.
          Six cards is a long scroll, and sending someone back to the top to act
          is the commonest way a page like this loses the click it was built
          for. Right-aligned rather than centred: it sits where the eye leaves
          the last card. Deliberately identical to the masthead's - same
          onStart, same label - so it reads as the same action met again, not a
          different one. Stacks and centres on a phone; see .welcome-tail. */}
      <div className="welcome-tail">
        <p style={{ margin: 0, fontSize: '13px', color: C.ink }}>
          {t('welcome_next')}
        </p>

        <Button variant="primary" icon={ArrowRightIcon} onClick={onStart}>
          {t('welcome_cta')}
        </Button>
      </div>

      <p style={{ margin: '16px 0 0', textAlign: 'center', fontSize: '13px', color: C.muted }}>
        {t('welcome_questions')}{' '}
        <button type="button" className="welcome-link" onClick={openSupport}>
          {t('welcome_talk_to_us')}
        </button>

        {/* Removed entirely on Wix, not hidden: App Market apps must bill
            through Wix Billing, and pointing a Wix merchant at our own checkout
            is a documented rejection trigger. */}
        {!isWix() && (
          <>
            <span aria-hidden="true" style={{ margin: '0 8px', color: '#b5b5b5' }}>·</span>
            <Link className="welcome-link" to="/Pricing">{t('welcome_see_pricing')}</Link>
          </>
        )}
      </p>
    </div>
  );
}
